import { z } from 'zod';
import { expenseProjectName } from './expenseProject';

export const EXPENSE_SORT_FIELDS = [
  'paidDate', 'expenseDate', 'store', 'category', 'description',
  'project', 'amount', 'paymentMethod', 'reimburse',
] as const;

export type ExpenseSortField = (typeof EXPENSE_SORT_FIELDS)[number];
export type SortDirection = 'asc' | 'desc';
export interface ExpenseSort { field: ExpenseSortField; direction: SortDirection; }

export const DEFAULT_EXPENSE_SORT: ExpenseSort = { field: 'paidDate', direction: 'desc' };

export const expenseSortSchema = z.object({
  field: z.enum(EXPENSE_SORT_FIELDS),
  direction: z.enum(['asc', 'desc']),
});

/** Minimal structural shape sortExpenses reads. Both ExpenseWithDetails (frontend)
 *  and the eager-loaded backend row satisfy it. `amount` may be number or numeric string. */
export interface SortableExpense {
  id: string;
  paidDate: string | null;
  expenseDate: string | null;
  storeName: string | null;
  description: string | null;
  amount: number | string | null;
  paymentMethod: string | null;
  reimburse: boolean;
  category: { name: string | null } | null;
  /** Name written at save time; null on pre-document-model rows. */
  projectName?: string | null;
  /** Legacy relation, still the only project source for those older rows. */
  project: { name: string | null } | null;
}

type Key = string | number | null | undefined;

const KEY_OF: Record<ExpenseSortField, (e: SortableExpense) => Key> = {
  paidDate: (e) => e.paidDate,
  expenseDate: (e) => e.expenseDate,
  store: (e) => e.storeName,
  category: (e) => e.category?.name ?? null,
  description: (e) => e.description,
  project: (e) => expenseProjectName(e),
  amount: (e) => (e.amount == null ? null : Number(e.amount)),
  paymentMethod: (e) => e.paymentMethod,
  reimburse: (e) => (e.reimburse ? 1 : 0),
};

const NUMERIC: Record<ExpenseSortField, boolean> = {
  paidDate: false, expenseDate: false, store: false, category: false,
  description: false, project: false, amount: true, paymentMethod: false, reimburse: true,
};

function compareKeys(av: Key, bv: Key, direction: SortDirection, numeric: boolean): number {
  const aEmpty = av == null || av === '';
  const bEmpty = bv == null || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const base = numeric
    ? (av as number) - (bv as number)
    : String(av).localeCompare(String(bv));
  return direction === 'asc' ? base : -base;
}

/** Pure sort. Empty/null keys pinned last in both directions; ties broken by id. */
export function sortExpenses<T extends SortableExpense>(rows: T[], sort: ExpenseSort): T[] {
  const get = KEY_OF[sort.field];
  const numeric = NUMERIC[sort.field];
  return [...rows].sort((a, b) => {
    const primary = compareKeys(get(a), get(b), sort.direction, numeric);
    if (primary !== 0) return primary;
    return String(a.id).localeCompare(String(b.id));
  });
}
